/**
 * The Rules Reference.
 *
 * **Every answer carries its citation**, and an answer that is not in the book
 * says so plainly rather than guessing. That is the whole design: the lookup
 * is deterministic and server-side, never a model call, because a rules
 * question answered by an LLM is a rules question answered wrongly at some
 * unknowable rate — and the wrong answers would be the plausible ones.
 *
 * The GM-side files are not reachable from here. The endpoint answers a
 * request for one with the same "not in the book" a genuine miss gets, so the
 * client cannot tell them apart either, which is the point.
 */

import { useEffect, useState } from 'react';

import { api } from '../api.js';

interface ReferenceHit {
  file: string;
  /** The dotted path inside the file — this is the citation. */
  path: string;
  name: string;
  excerpt: string;
}

interface SearchResult {
  query: string;
  hits: ReferenceHit[];
  notInTheBook: boolean;
}

/** How much of one section to render before it stops being readable. */
const MAX_SECTION_CHARS = 20_000;

export function truncate(text: string): string {
  return text.length <= MAX_SECTION_CHARS
    ? text
    : `${text.slice(0, MAX_SECTION_CHARS)}\n\n… truncated. Search for the rule you want; every hit carries its citation.`;
}

export function RulesPage() {
  const [files, setFiles] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<SearchResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [section, setSection] = useState<{ citation: string; data: unknown } | null>(null);

  useEffect(() => {
    void api
      .get<{ files: string[] }>('/api/rules/files')
      .then((body) => setFiles(body.files))
      .catch(() => setError('the rules index could not be loaded'));
  }, []);

  const search = (term: string) => {
    setSection(null);
    setError(null);
    if (term.trim().length < 2) {
      setResult(null);
      return;
    }
    void api
      .get<SearchResult>(`/api/rules/search?q=${encodeURIComponent(term)}`)
      .then(setResult)
      .catch(() => setError('the search could not be run'));
  };

  const openSection = (file: string, path?: string) => {
    setError(null);
    void api
      .get<{ citation: string; data: unknown }>(
        `/api/rules/${encodeURIComponent(file)}${path ? `?path=${encodeURIComponent(path)}` : ''}`,
      )
      .then(setSection)
      // 404 is the honest answer for both a miss and a GM-side file, and it
      // reads the same here on purpose.
      .catch(() => setError('not in the book'));
  };

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Rules Reference</h1>

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          search(query);
        }}
      >
        <label className="sr-only" htmlFor="rules-search">
          Search the rules
        </label>
        <input
          id="rules-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="morale, backstab, encumbrance…"
          className="flex-1 rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
        />
        <button
          type="submit"
          className="rounded-md border border-neutral-300 px-4 py-2 text-sm font-medium dark:border-neutral-700"
        >
          Look up
        </button>
      </form>

      {error && (
        <p role="alert" className="text-sm text-amber-800 dark:text-amber-300">
          {error}
        </p>
      )}

      {result?.notInTheBook && (
        // Stated plainly. The alternative — an empty list — reads as a broken
        // search rather than as an answer.
        <p className="text-sm">Not in the book.</p>
      )}

      {result && result.hits.length > 0 && (
        <ol className="space-y-2 text-sm">
          {result.hits.map((hit) => (
            <li key={`${hit.file}:${hit.path}`} className="space-y-1">
              <button
                type="button"
                onClick={() => openSection(hit.file, hit.path)}
                className="text-left font-medium underline underline-offset-2"
              >
                {hit.name}
              </button>
              <p className="text-xs text-neutral-500 dark:text-neutral-400">
                {hit.file}.json → {hit.path}
              </p>
              <p className="text-neutral-700 dark:text-neutral-300">{hit.excerpt}</p>
            </li>
          ))}
        </ol>
      )}

      {section && (
        <section className="space-y-1">
          {/* The citation, always. A rule without one is unverified. */}
          <p className="text-xs text-neutral-500 dark:text-neutral-400">{section.citation}</p>
          {/* Capped. A whole spell file is 312 entries, and a reference that
              pastes all of them is not one — the search above is how you find
              a rule; this is how you read the one you found. */}
          <pre className="overflow-x-auto rounded-md border border-neutral-200 p-3 text-xs dark:border-neutral-800">
            {truncate(JSON.stringify(section.data, null, 2))}
          </pre>
        </section>
      )}

      <details>
        <summary className="cursor-pointer text-sm text-neutral-600 dark:text-neutral-400">
          Browse by file ({files.length})
        </summary>
        <ul className="mt-2 flex flex-wrap gap-2 text-sm">
          {files.map((file) => (
            <li key={file}>
              <button
                type="button"
                onClick={() => openSection(file)}
                className="rounded-md border border-neutral-300 px-2 py-1 dark:border-neutral-700"
              >
                {file}
              </button>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
